import { deleteUser } from 'api/user';
import AppLoader from 'components/AppLoader';
import Button from 'components/Button';
import Container from 'components/Container';
import { Modal } from 'components/Modal';
import Text from 'components/Text';
import useAlert from 'hooks/useAlert';
import { useState } from 'react';
import useStores from 'stores/helpers/useStores';
import styled, { useTheme } from 'styled-components';
import { IPlayer } from 'types/Player';

const Wrapper = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.palette.dark[50]};
  background-color: ${(props) => props.theme.palette.light[50]};
  display: flex;
  justify-content: center;
`;

const StyledContainer = styled(Container)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BoldText = styled.span`
  font-weight: bold;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
`;

interface Props {
  user: IPlayer;
}

export const UserItem: React.FC<Props> = ({ user }) => {
  const theme = useTheme();
  const alert = useAlert();

  const [isOpen, setIsOpen] = useState(false);

  const { playerStore } = useStores();
  const [loading, setLoading] = useState(false);

  const onDelete = () => {
    setIsOpen(true);
  };

  const onDeleteConfirm = async () => {
    setLoading(true);
    try {
      await deleteUser(user.userId);
      alert.success('User successfully deleted');
      await playerStore.loadPlayers();
    } catch (e) {
      alert.error('Failed to delete user');
      setLoading(false);
    }
  };

  return (
    <>
      <Wrapper>
        <StyledContainer py={12}>
          <Text
            variant="headline2"
            textTransform="uppercase"
            color={theme.palette.dark[800]}
          >
            {user.firstName} {user.lastName}
          </Text>

          <Button size="small" width="xs" onClick={onDelete}>
            Delete
          </Button>
        </StyledContainer>
      </Wrapper>

      <Modal isOpen={isOpen}>
        <Text variant="h3" mb={12}>
          Confirm deletion
        </Text>
        <Text variant="p1" mb={24}>
          Are you sure you want to delete user{' '}
          <BoldText>
            {user.firstName} {user.lastName}
          </BoldText>
          ?
        </Text>

        <ButtonContainer>
          <Button
            variant="outline"
            color="primary"
            size="small"
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="solid"
            color="secondary"
            onClick={onDeleteConfirm}
          >
            Confirm
          </Button>
        </ButtonContainer>
      </Modal>

      {loading && <AppLoader />}
    </>
  );
};
